// Faction-select menu, post-battle result screen, and a campaign entry screen.

import { FACTIONS } from "../sim/data";
import type { Faction } from "../sim/types";
import { ScreenBase } from "./screen";
import type { BattleResult } from "./game";
import { roundRectPath } from "../render/art";

export interface StartOpts {
  spectate?: boolean;
  demo?: boolean;
}

export class MenuScene extends ScreenBase {
  private selected: Faction = "legion";
  private onStart: (f: Faction, o: StartOpts) => void;
  private onCampaign: (f: Faction) => void;

  constructor(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, onStart: (f: Faction, o: StartOpts) => void, onCampaign: (f: Faction) => void) {
    super(canvas, ctx);
    this.onStart = onStart;
    this.onCampaign = onCampaign;
  }

  draw(time: number): void {
    const ctx = this.ctx;
    this.clearCursor();
    background(ctx, this.vw, this.vh, time);

    // title
    ctx.textAlign = "center";
    ctx.fillStyle = "#e8e0d4";
    ctx.font = "800 clamp(40px, 6vw, 72px) Trebuchet MS";
    ctx.font = `800 ${Math.min(72, this.vw * 0.07)}px Trebuchet MS`;
    ctx.fillText("ASHFALL: DOMINION", this.vw / 2, this.vh * 0.16);
    ctx.fillStyle = "#b23040";
    ctx.font = "600 18px Trebuchet MS";
    ctx.fillText("Squad-tactics for a shattered continent — Vertical Slice", this.vw / 2, this.vh * 0.16 + 34);
    ctx.textAlign = "left";

    // faction cards
    const cardW = Math.min(400, (this.vw - 120) / 2);
    const cardH = Math.min(320, this.vh * 0.42);
    const gap = 40;
    const totalW = cardW * 2 + gap;
    const x0 = this.vw / 2 - totalW / 2;
    const y0 = this.vh * 0.28;
    this.factionCard("legion", x0, y0, cardW, cardH);
    this.factionCard("kindled", x0 + cardW + gap, y0, cardW, cardH);

    // buttons
    const by = y0 + cardH + 34;
    const bw = 260;
    const meta = FACTIONS[this.selected];
    this.button(this.vw / 2 - bw / 2, by, bw, 48, `Start Battle — ${meta.name}`, () => this.onStart(this.selected, {}), { primary: true, color: meta.colors.primary });
    this.button(this.vw / 2 - bw / 2, by + 58, bw / 2 - 6, 40, "Watch Demo", () => this.onStart(this.selected, { spectate: true, demo: true }));
    this.button(this.vw / 2 + 6, by + 58, bw / 2 - 6, 40, "Campaign", () => this.onCampaign(this.selected));
  }

  private factionCard(f: Faction, x: number, y: number, w: number, h: number): void {
    const ctx = this.ctx;
    const meta = FACTIONS[f];
    const sel = this.selected === f;
    ctx.fillStyle = sel ? "rgba(40,36,30,0.96)" : "rgba(24,22,18,0.92)";
    roundRectPath(ctx, x, y, w, h, 10);
    ctx.fill();
    ctx.strokeStyle = sel ? meta.colors.primary : "rgba(120,112,96,0.5)";
    ctx.lineWidth = sel ? 3 : 1.5;
    ctx.stroke();

    // color swatch
    ctx.fillStyle = meta.colors.primary;
    roundRectPath(ctx, x, y, w, 8, 4);
    ctx.fill();

    ctx.fillStyle = meta.colors.trim;
    ctx.font = "800 26px Trebuchet MS";
    ctx.fillText(meta.name, x + 22, y + 46);

    ctx.fillStyle = "#cfc7ba";
    ctx.font = "500 14px Trebuchet MS";
    wrapText(ctx, meta.blurb, x + 22, y + 74, w - 44, 19);

    ctx.fillStyle = meta.colors.trim;
    ctx.font = "700 13px Trebuchet MS";
    ctx.fillText(f === "legion" ? "DOCTRINE" : "FERVOR", x + 22, y + h - 78);
    ctx.fillStyle = "#b0a894";
    ctx.font = "500 13px Trebuchet MS";
    wrapText(ctx, meta.hook, x + 22, y + h - 60, w - 44, 18);

    this.regions.push({ x, y, w, h, fn: () => (this.selected = f) });
    if (this.mouse.x >= x && this.mouse.x <= x + w && this.mouse.y >= y && this.mouse.y <= y + h) this.canvas.style.cursor = "pointer";
  }
}

export class ResultScene extends ScreenBase {
  private result: BattleResult;
  private playerFaction: Faction;
  private onRematch: () => void;
  private onMenu: () => void;

  constructor(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, result: BattleResult, playerFaction: Faction, onRematch: () => void, onMenu: () => void) {
    super(canvas, ctx);
    this.result = result;
    this.playerFaction = playerFaction;
    this.onRematch = onRematch;
    this.onMenu = onMenu;
  }

  draw(time: number): void {
    const ctx = this.ctx;
    this.clearCursor();
    background(ctx, this.vw, this.vh, time);
    const won = this.result.phase === "won";
    ctx.textAlign = "center";
    ctx.fillStyle = won ? "#8fd07a" : "#d0483c";
    ctx.font = `800 ${Math.min(96, this.vw * 0.09)}px Trebuchet MS`;
    ctx.fillText(won ? "VICTORY" : "DEFEAT", this.vw / 2, this.vh * 0.32);

    const meta = FACTIONS[this.result.winner ?? this.playerFaction];
    ctx.fillStyle = "#cfc7ba";
    ctx.font = "600 20px Trebuchet MS";
    ctx.fillText(`${meta.name} holds the field`, this.vw / 2, this.vh * 0.32 + 44);

    const s = this.result.world.stats;
    const t = this.result.world.time;
    ctx.font = "500 16px Trebuchet MS";
    ctx.fillStyle = "#b0a894";
    const line = `Time ${Math.floor(t / 60)}:${Math.floor(t % 60).toString().padStart(2, "0")}   ·   Casualties ${s.kills}   ·   Morale breaks ${s.moraleBreaks}   ·   Frenzies ${s.frenzies}   ·   Captures ${s.captures}   ·   Squads wiped ${s.squadsWiped}`;
    ctx.fillText(line, this.vw / 2, this.vh * 0.32 + 84);
    ctx.textAlign = "left";

    const by = this.vh * 0.6;
    this.button(this.vw / 2 - 220, by, 210, 48, "Rematch", () => this.onRematch(), { primary: true });
    this.button(this.vw / 2 + 10, by, 210, 48, "Main Menu", () => this.onMenu());
  }
}

// ---- shared ----------------------------------------------------------------

export function background(ctx: CanvasRenderingContext2D, vw: number, vh: number, time: number): void {
  const g = ctx.createLinearGradient(0, 0, 0, vh);
  g.addColorStop(0, "#1a1613");
  g.addColorStop(1, "#0a0908");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, vw, vh);
  // drifting embers
  ctx.fillStyle = "rgba(200,84,18,0.5)";
  for (let i = 0; i < 40; i++) {
    const x = (i * 137.5 + time * 12 * (0.4 + (i % 5) * 0.15)) % vw;
    const y = (vh - ((time * (20 + (i % 7) * 6) + i * 90) % (vh + 100)));
    ctx.globalAlpha = 0.3 + 0.3 * Math.sin(time * 3 + i);
    ctx.beginPath();
    ctx.arc(x, y, 1.5 + (i % 3), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

export function wrapText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxW: number, lineH: number): void {
  const words = text.split(" ");
  let line = "";
  let yy = y;
  for (const word of words) {
    const test = line ? line + " " + word : word;
    if (ctx.measureText(test).width > maxW && line) {
      ctx.fillText(line, x, yy);
      line = word;
      yy += lineH;
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, yy);
}
